import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as z from 'zod';

import { ArrowUpDown } from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { DateRangePicker } from '@repo/components/date-picker';
import { parseDateRange, serializeDateRange } from '@repo/components/date-picker/utils';
import type { DateRange } from '@repo/components/date-picker/utils';
import { LoadingIndicator } from '@repo/components/loading-indicator';
import { Menu, MenuItem, MenuSection, MenuTrigger } from '@repo/components/menu';
import { Pagination } from '@repo/components/pagination';
import { Popover } from '@repo/components/popover';
import { SearchField } from '@repo/components/search-field';

import { PageLoading } from '@repo/features/page-loading';

import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import { queryCertificates } from '~/services/certificate/certificate.ts';

import { CertificatesTable } from './-components/certificates-table.tsx';

const SORTING_OPTIONS = {
  newest: { key: 'newest', title: 'Newest first' },
  oldest: { key: 'oldest', title: 'Oldest first' },
} as const;

const PAGE_SIZE = 25;

const searchSchema = z.object({
  sortBy: z.enum([SORTING_OPTIONS.newest.key, SORTING_OPTIONS.oldest.key]).catch(SORTING_OPTIONS.newest.key),
  search: z.string().catch(''),
  dateRange: z
    .object({
      start: z.iso.date(),
      end: z.iso.date(),
    })
    .optional()
    .catch(undefined),
  page: z.number().min(0).catch(0),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/project/$project/(certificates)/certificates/')({
  validateSearch: searchSchema.catch(() => searchSchema.parse({})),
  component: RouteCertificates,
  errorComponent: () => <PageError msg="An error occurred while loading certificates." />,
});

function RouteCertificates() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { project: projectId } = Route.useParams();
  const {
    data,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useQuery(queryCertificates(projectId));

  const [searchTerm, setSearchTerm] = useState(search.search);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const [dateRange, setDateRange] = useState<DateRange | null>(() => parseDateRange(search.dateRange));

  const [pageCount, setPageCount] = useState(0);
  const [filteredDataCount, setFilteredDataCount] = useState(data?.length ?? 0);

  const clearFilter = () => {
    setDateRange(null);
    setSearchTerm('');
  };

  const pagination = useMemo(() => ({
    pageIndex: search.page,
    pageSize: PAGE_SIZE,
  }), [search.page]);

  const sortingState = useMemo<SortingState>(
    () => [{ id: 'issued', desc: search.sortBy === 'newest' }],
    [search.sortBy],
  );

  const columnFilters = useMemo(
    () => [
      {
        id: 'issued',
        value: {
          from: search.dateRange?.start ? new Date(search.dateRange.start) : undefined,
          to: search.dateRange?.end ? new Date(search.dateRange.end) : undefined,
        },
      },
    ],
    [search.dateRange],
  );

  const updateSearchParams = useCallback(
    (params: Partial<SearchParams>) => {
      void navigate({ to: '.', search: prev => ({ ...prev, ...params }) });
    },
    [navigate],
  );

  useEffect(() => {
    const serializedDateRange = serializeDateRange(dateRange);
    const searchChanged = debouncedSearchTerm !== search.search;
    const dateChanged = search.dateRange?.start !== serializedDateRange?.start
      || search.dateRange?.end !== serializedDateRange?.end;

    if (searchChanged || dateChanged) {
      updateSearchParams({
        search: debouncedSearchTerm,
        dateRange: serializedDateRange,
        page: 0,
      });
    }
  }, [dateRange, debouncedSearchTerm, search.search, search.dateRange, updateSearchParams]);

  const renderPaginationInfo = () => {
    if (pageCount <= 1)
      return filteredDataCount;

    const start = search.page * PAGE_SIZE + 1;
    const end = Math.min(search.page * PAGE_SIZE + PAGE_SIZE, filteredDataCount);

    return `${start}-${end}`;
  };

  if (isError) {
    return (
      <PageError
        msg="An error occurred while loading certificates."
        retry={() => {
          void refetch();
        }}
      />
    );
  }

  if (isLoading) {
    return <PageLoading />;
  }

  if (!data) {
    return <p>No certificates found.</p>;
  }

  return (
    <div className="flex flex-col items-start gap-6 pb-6">
      <ContentHeader>Certificates</ContentHeader>

      <div className="grid w-full sm:flex sm:flex-wrap items-start gap-4">
        <SearchField
          aria-label="Search certificates"
          placeholder="Search certificates"
          value={searchTerm}
          onChange={setSearchTerm}
        />

        <DateRangePicker
          aria-label="Filter certificates by date range"
          value={dateRange}
          onChange={(dates) => { setDateRange(dates); }}
        />

        <MenuTrigger>
          <Button
            size="md"
            icon={{ component: <ArrowUpDown size={16} strokeWidth={1.5} /> }}
            variant="soft"
          >
            {SORTING_OPTIONS[search.sortBy].title}
          </Button>
          <Popover>
            <Menu>
              <MenuSection title="Sort by">
                {Object.values(SORTING_OPTIONS).map(option => (
                  <MenuItem key={option.key} onAction={() => { updateSearchParams({ sortBy: option.key }); }}>
                    {option.title}
                  </MenuItem>
                ))}
              </MenuSection>
            </Menu>
          </Popover>
        </MenuTrigger>
      </div>

      <div className="w-full space-y-2">
        <Pagination
          currentPage={search.page}
          pageCount={pageCount}
          onChange={(page) => { updateSearchParams({ page }); }}
        />

        <Card className="max-w-full w-fit p-0">
          {pageCount > 1 && (
            <div className="p-4 pb-0">
              <div className="flex items-center gap-4">
                {isFetching && <LoadingIndicator size={16} className="text-primary-700" />}
                <p>
                  Showing
                  {' '}
                  {renderPaginationInfo()}
                  {' '}
                  of
                  {' '}
                  {filteredDataCount}
                  {' '}
                  certificates
                </p>
              </div>
            </div>
          )}
          <CertificatesTable
            certificates={data}
            pagination={pagination}
            sorting={sortingState}
            columnFilters={columnFilters}
            setPageCount={setPageCount}
            setFilteredDataCount={setFilteredDataCount}
            globalFilter={search.search}
            clearFilter={clearFilter}
          />
        </Card>

        <Pagination
          currentPage={search.page}
          pageCount={pageCount}
          onChange={(page) => { updateSearchParams({ page }); }}
        />
      </div>
    </div>
  );
}
